'use client'

import { formatDateNormal, formatImageURL, getVendorPrimaryColor, resolveColorHex } from "@/utils/util"
import { getReadingTime } from "@/utils/blog"
import { getFacebookShareUrl, getTwitterShareUrl, openSharePopup, copyToClipboard } from "@/utils/share"
import { navigateUrl } from "@/utils/client-request"
import { motion } from "framer-motion"
import { ArrowRight, ChevronRight } from "lucide-react"
import { usePathname } from "next/navigation"
import { toast } from "@/lib/toast"

export default function BlogDetail({ blog, related = [], vendor }: { blog: any, related?: any[], vendor?: any }) {
  const pathname = usePathname()
  const readMin = getReadingTime(blog?.content || '')
  const primary = resolveColorHex(getVendorPrimaryColor(vendor))

  const onShareFacebook = () => openSharePopup(getFacebookShareUrl(window.location.href))
  const onShareTwitter = () => openSharePopup(getTwitterShareUrl(window.location.href, blog?.title || ''))
  const onCopyLink = async () => {
    const ok = await copyToClipboard(window.location.href)
    if (ok) toast.success('Холбоос хуулагдлаа')
    else toast.error('Хуулж чадсангүй')
  }

  return (
    <motion.div initial={{ opacity: 0, y: -10 }} whileInView={{ opacity: 1, y: 0 }} transition={{ delay: 0, duration: 0.5, ease: "easeIn" }} className="bg-[#fafafa]">
      <div className="mx-auto w-full max-w-[1280px] px-4 lg:px-8 pt-6">
        <div className="hidden md:flex items-center gap-1.5 text-xs text-muted-foreground">
          <a href={navigateUrl('/', pathname)} className="hover:text-foreground">Нүүр</a>
          <ChevronRight className="h-3 w-3 opacity-50" />
          <a href={navigateUrl('/blog', pathname)} className="hover:text-foreground">Блог</a>
          <ChevronRight className="h-3 w-3 opacity-50" />
          <span className="text-foreground font-medium line-clamp-1">{blog.title}</span>
        </div>
      </div>

      <div className="mx-auto w-full max-w-[1280px] px-4 lg:px-8 py-6 grid grid-cols-1 lg:grid-cols-12 gap-6">
        <div className="lg:col-span-8">
          <article className="bg-white rounded-2xl border border-border overflow-hidden">
            <div className="aspect-[16/9] bg-muted">
              <img src={formatImageURL(blog.thumbnail || 'placeholder.png')} alt={blog.title} className="w-full h-full object-cover" />
            </div>
            <div className="p-5 sm:p-6 lg:p-10">
              <div className="text-xs text-muted-foreground flex items-center gap-3 tabular-nums">
                <span>{formatDateNormal(new Date(blog.createdAt))}</span>
                <span>·</span>
                <span>{readMin} мин уншина</span>
              </div>
              <h1 className="mt-3 text-[28px] sm:text-[36px] lg:text-[44px] font-bold tracking-[-0.015em] leading-[1.1] text-foreground">{blog.title}</h1>

              <div className="mt-8 text-foreground blog-detail leading-8 prose max-w-none" dangerouslySetInnerHTML={{ __html: blog.content }} />

              <div className="mt-10 pt-6 border-t border-border flex items-center justify-end">
                <ShareRow onFacebook={onShareFacebook} onTwitter={onShareTwitter} onCopy={onCopyLink} />
              </div>
            </div>
          </article>
        </div>

        <aside className="lg:col-span-4">
          <div className="lg:sticky lg:top-36 space-y-5">
            {related.length > 0 && (
              <div className="bg-white rounded-2xl border border-border p-5">
                <div className="text-[11px] tracking-[0.22em] uppercase font-semibold text-muted-foreground mb-4">Бусад нийтлэлүүд</div>
                <div className="space-y-4">
                  {related.map((p) => (
                    <a key={p.id} href={navigateUrl(`/blog/${p.id}`, pathname)} className="group flex gap-3 items-start">
                      <div className="aspect-square w-20 sm:w-24 rounded-xl overflow-hidden bg-muted shrink-0">
                        <img src={formatImageURL(p.thumbnail || 'placeholder.png')} alt={p.title} className="w-full h-full object-cover transition-transform group-hover:scale-105" />
                      </div>
                      <div className="min-w-0 flex-1">
                        <h4 className="text-sm font-semibold leading-snug line-clamp-2 group-hover:underline">{p.title}</h4>
                        <div className="mt-1.5 text-xs text-muted-foreground tabular-nums">
                          {formatDateNormal(new Date(p.createdAt))} · {getReadingTime(p.content)} мин
                        </div>
                      </div>
                    </a>
                  ))}
                </div>
              </div>
            )}

            <a href={navigateUrl('/product', pathname)} className="block rounded-2xl p-5 text-white" style={{ background: primary }}>
              <div className="text-[11px] tracking-[0.22em] uppercase font-semibold opacity-80">{vendor?.name || 'Дэлгүүр'}</div>
              <div className="mt-2 text-[18px] font-semibold leading-snug">Бүтээгдэхүүн үзэх үү?</div>
              <p className="mt-1 text-[13px] opacity-90">Манай дэлгүүрийн шинэ бүтээгдэхүүнүүдтэй танилцана уу.</p>
              <span className="mt-4 inline-flex items-center gap-1.5 h-9 px-3.5 rounded-full bg-white text-[13px] font-semibold" style={{ color: primary }}>
                Бүтээгдэхүүн үзэх <ArrowRight className="h-3.5 w-3.5" />
              </span>
            </a>
          </div>
        </aside>
      </div>
    </motion.div>
  )
}

function ShareRow({ onFacebook, onTwitter, onCopy }: { onFacebook: () => void, onTwitter: () => void, onCopy: () => void }) {
  return (
    <div className="flex items-center gap-2">
      <button
        onClick={onFacebook}
        className="inline-flex items-center justify-center size-9 rounded-full bg-gray-100 text-gray-600 hover:bg-gray-200 hover:text-gray-900 transition-colors cursor-pointer"
        aria-label="Share on Facebook"
      >
        <svg fill="currentColor" viewBox="0 0 24 24" className="size-5">
          <path fillRule="evenodd" d="M22 12c0-5.523-4.477-10-10-10S2 6.477 2 12c0 4.991 3.657 9.128 8.438 9.878v-6.987h-2.54V12h2.54V9.797c0-2.506 1.492-3.89 3.777-3.89 1.094 0 2.238.195 2.238.195v2.46h-1.26c-1.243 0-1.63.771-1.63 1.562V12h2.773l-.443 2.89h-2.33v6.988C18.343 21.128 22 16.991 22 12z" clipRule="evenodd" />
        </svg>
      </button>
      <button
        onClick={onTwitter}
        className="inline-flex items-center justify-center size-9 rounded-full bg-gray-100 text-gray-600 hover:bg-gray-200 hover:text-gray-900 transition-colors cursor-pointer"
        aria-label="Share on X"
      >
        <svg fill="currentColor" viewBox="0 0 24 24" className="size-5">
          <path d="M13.6823 10.6218L20.2391 3H18.6854L12.9921 9.61788L8.44486 3H3.2002L10.0765 13.0074L3.2002 21H4.75404L10.7663 14.0113L15.5685 21H20.8131L13.6819 10.6218H13.6823ZM11.5541 13.0956L10.8574 12.0991L5.31391 4.16971H7.70053L12.1742 10.5689L12.8709 11.5655L18.6861 19.8835H16.2995L11.5541 13.096V13.0956Z" />
        </svg>
      </button>
      <button
        onClick={onCopy}
        className="inline-flex items-center justify-center size-9 rounded-full bg-gray-100 text-gray-600 hover:bg-gray-200 hover:text-gray-900 transition-colors cursor-pointer"
        aria-label="Copy link"
      >
        <svg fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" className="size-4">
          <path strokeLinecap="round" strokeLinejoin="round" d="M13.19 8.688a4.5 4.5 0 0 1 1.242 7.244l-4.5 4.5a4.5 4.5 0 0 1-6.364-6.364l1.757-1.757m13.35-.622 1.757-1.757a4.5 4.5 0 0 0-6.364-6.364l-4.5 4.5a4.5 4.5 0 0 0 1.242 7.244" />
        </svg>
      </button>
    </div>
  )
}
